import React, { useState, useEffect, useCallback, useMemo, useRef, Suspense } from 'react';
import { sessionCache } from '../services/sessionCache';
import { JWTService } from '../services/jwtService';
import { createCareDailyService, createCareDailyServiceForDemo } from '../services/careDailyService';
import { MetricConfigService } from '../services/supabaseClient';
import { JWTPayload, MetricConfig } from '../types';
import { CategoryService, DashboardCategory } from '../services/categoryService';
import { DimensionService, WellnessDimension } from '../services/dimensionService';
import { computeDimensionScore } from '../utils/dimensionScoring';
import { buildWheelTrends } from '../utils/wheelTrends';
import { withWheelColors } from '../utils/sliceColors';
import { isDemoMode, getDemoModeMessage } from '../services/demoDataService';
import { ProgramSettingsService, ProgramSettings } from '../services/programSettingsService';
import { TabNavigation } from './TabNavigation';
import { MetricCard } from './MetricCard';
import { ChunkBoundary } from './ChunkBoundary';
import { ActivityLogger } from './ActivityLogger';
import { GoalsTracker } from './GoalsTracker';
import { EventRecommendations } from './EventRecommendations';
import { ResidentSelector } from './ResidentSelector';
import { ProgressLeader } from './ProgressLeader';
import { Icon } from './Icon';
import { ResidentNeed, WellnessDimension as RecWellnessDimension } from '../types/recommendations';
import { createGoIconApiService } from '../services/goiconApiService';
import { GoIconActivitiesService } from '../services/goiconActivitiesService';
// import { WellnessGauge } from './WellnessGauge';
import { LoadingSpinner } from './LoadingSpinner';
import { ErrorMessage } from './ErrorMessage';
import { SelfAssessmentModal } from './SelfAssessmentModal';
import { SelfAssessmentConfigService, SelfAssessmentConfig } from '../services/selfAssessmentConfigService';
import { SelfAssessmentResponseService, LatestResponseMap } from '../services/selfAssessmentResponseService';
import { ResidentHelpContentService, ResidentHelpContent, HelpSection, DEFAULT_SCORE_INFO, buildScoreInfoSections } from '../services/residentHelpContentService';
import { ScoreInfoDialog } from './ScoreInfoDialog';
import { PrivacyFaq } from './PrivacyFaq';
// import { Tooltip } from './Tooltip';
import { debugLog } from '../utils/debugLog';
import { deriveMetricCard, lookupMetricData } from '../utils/metricStatus';
import './Dashboard.css';

// How long to wait after logging an activity before re-reading the wheel data.
// CareDaily suggested 3–5 seconds.
const WHEEL_REFRESH_DELAY_MS = 4000;

// WellnessPieChart pulls in recharts (see Task 11 / docs/perf-baseline.md) — lazy
// so it lands in the shared recharts async chunk instead of the resident bundle.
// Recreated (not just referenced) on chunk-load retry: React.lazy() caches a
// rejected import forever on the lazy reference itself, so only constructing a
// fresh React.lazy() actually re-triggers the dynamic import.
function makeLazyWellnessPieChart() {
  return React.lazy(() => import('./charts/WellnessPieChart'));
}

interface DashboardState {
  loading: boolean;
  error: string | null;
  careDailyConnectionError: string | null;
  jwtPayload: JWTPayload | null;
  wellnessData: any;
  timeSeriesData: Record<string, any[]> | null;
  metricConfigs: MetricConfig[];
  categories: DashboardCategory[];
  dimensions: WellnessDimension[];
  activeTab: string;
  programName: string;
  primaryColor: string;
  secondaryColor: string;
  programSettings: ProgramSettings | null;
  currentLocationId: string | null; // CareDaily locationId for the current resident being viewed
  currentExternalId: string | null; // GoIcon external ID for the current resident being viewed
}

interface DashboardProps {
  jwtPayload: JWTPayload | null;
  isStaff: boolean;
  onNavigateToAdmin?: () => void;
}

export const Dashboard: React.FC<DashboardProps> = ({ jwtPayload: initialJwtPayload, isStaff, onNavigateToAdmin }) => {
  const [state, setState] = useState<DashboardState>({
    loading: true,
    error: null,
    careDailyConnectionError: null,
    jwtPayload: initialJwtPayload,
    wellnessData: null,
    timeSeriesData: null,
    metricConfigs: [],
    categories: [],
    dimensions: [],
    activeTab: 'overview',
    programName: 'Successful Aging',
    primaryColor: '#667eea',
    secondaryColor: '#764ba2',
    programSettings: null,
    currentLocationId: null,
    currentExternalId: null
  });
  const initializeRequestIdRef = useRef(0);
  const wheelRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => () => {
    if (wheelRefreshTimerRef.current) clearTimeout(wheelRefreshTimerRef.current);
  }, []);
  const [overviewExpandedSection, setOverviewExpandedSection] = useState<'activity' | 'goals' | 'recommendations' | null>(null);

  // Owned in state so a chunk-load retry can swap in a fresh React.lazy() ref.
  const [LazyWellnessPieChart, setLazyWellnessPieChart] = useState(() => makeLazyWellnessPieChart());
  const handlePieChartRetry = useCallback(() => setLazyWellnessPieChart(makeLazyWellnessPieChart()), []);

  // Bumped by GoalsTracker whenever the resident's goal set changes, so
  // goal-driven event recommendations re-score without a reload.
  const [goalsVersion, setGoalsVersion] = useState(0);

  // Admin-editable resident help content (score info / privacy FAQ dialogs)
  const [helpContent, setHelpContent] = useState<ResidentHelpContent | null>(null);
  const [showScoreInfo, setShowScoreInfo] = useState(false);
  const [showPrivacyFaq, setShowPrivacyFaq] = useState(false);

  // Self-assessment state
  const [selfAssessmentConfig, setSelfAssessmentConfig] = useState<SelfAssessmentConfig | null>(null);
  const [selfAssessmentDue, setSelfAssessmentDue] = useState(false);
  const [showSelfAssessment, setShowSelfAssessment] = useState(false);
  const [, setLatestSelfAssessmentData] = useState<LatestResponseMap>({});

  // GoIcon activity-based dimension percentages (fallback when CareDaily is off)
  const [goiconDimensionPcts, setGoiconDimensionPcts] = useState<Record<string, number>>({});

  // Staff debug panel
  const [debugRawResponses, setDebugRawResponses] = useState<Record<string, any>>({});
  const [showDebugPanel, setShowDebugPanel] = useState(false);
  const [debugLocationOverride, setDebugLocationOverride] = useState<string>('');
  const [debugLocationInput, setDebugLocationInput] = useState<string>('');
  const [debugExternalIdOverride, setDebugExternalIdOverride] = useState<string>('');
  const [debugExternalIdInput, setDebugExternalIdInput] = useState<string>('');

  // For STAFF users: track which resident they're viewing.
  // Persisted in sessionStorage so navigating to admin and back restores the selection.
  const [selectedResidentId, setSelectedResidentIdState] = useState<string>(() => {
    if (initialJwtPayload?.role?.toUpperCase() === 'STAFF') {
      const facilityId = initialJwtPayload?.facilityId;
      const persisted = facilityId
        ? sessionCache.get<string>(`selected_resident_${facilityId}`)
        : null;
      return persisted ?? 'SAMPLE_DATA';
    }
    return initialJwtPayload?.residentId || '';
  });

  const setSelectedResidentId = useCallback((id: string) => {
    setSelectedResidentIdState(id);
    const facilityId = initialJwtPayload?.facilityId;
    if (facilityId && initialJwtPayload?.role?.toUpperCase() === 'STAFF') {
      sessionCache.set(`selected_resident_${facilityId}`, id);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialJwtPayload?.facilityId, initialJwtPayload?.role]);

  // Track GoIcon internal resident ID (for event recommendations)
  const [goiconResidentId, setGoiconResidentId] = useState<string>('');

  // Debug: Log when selectedResidentId changes
  useEffect(() => {
    console.log('🔄 selectedResidentId changed to:', selectedResidentId);
    console.log('🔄 goiconResidentId:', goiconResidentId);
  }, [selectedResidentId, goiconResidentId]);

  // When a staff user's JWT arrives after the initial render, selectedResidentId may have
  // been initialised to '' (because the payload wasn't available yet). Restore the persisted
  // selection or fall back to "Sample Data (Demo)".
  useEffect(() => {
    if (isStaff && !selectedResidentId) {
      const facilityId = initialJwtPayload?.facilityId;
      const persisted = facilityId
        ? sessionCache.get<string>(`selected_resident_${facilityId}`)
        : null;
      setSelectedResidentId(persisted ?? 'SAMPLE_DATA');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaff, selectedResidentId, initialJwtPayload?.facilityId, setSelectedResidentId]);

  // Notify the parent frame of height changes so the embedding iframe auto-resizes.
  useEffect(() => {
    if (window.self === window.top) return; // not embedded — skip
    const notify = () => {
      const height = document.documentElement.scrollHeight;
      window.parent.postMessage({ type: 'RESIZE', height }, '*');
    };
    notify();
    const ro = new ResizeObserver(notify);
    ro.observe(document.documentElement);
    return () => ro.disconnect();
  }, []);

  // Search state placeholder (search UI removed/disabled). Keep here when reintroducing search.

  const [dateRange, setDateRange] = useState<{
    days: number;
    label: string;
  }>({
    days: 90,
    label: 'Last 3 Months'
  });

  const [showDateRange, setShowDateRange] = useState(false);

  const initializeDashboard = useCallback(async () => {
    const requestId = ++initializeRequestIdRef.current;
    try {
      setState(prev => ({ ...prev, loading: true, error: null }));

      let jwtPayload: JWTPayload | null = initialJwtPayload;

      // If JWT not passed as prop, try to get it from URL/storage
      if (!jwtPayload) {
        // Check if demo mode is enabled
        if (isDemoMode()) {
          console.log('🎭 Demo Mode enabled - using mock data');
          jwtPayload = {
            residentId: 'DEMO-001',
            facilityId: 'demo_facility',
            externalId: 'demo_external_001',
            role: 'resident',
            iat: Math.floor(Date.now() / 1000),
            exp: Math.floor(Date.now() / 1000) + (24 * 60 * 60) // 24 hours
          };
          
          console.log('Demo payload:', {
            residentId: jwtPayload.residentId,
            facilityId: jwtPayload.facilityId,
            note: 'Using demo/mock data - no API calls will be made'
          });
        }
        // Check if JWT bypass is enabled for testing
        else if (process.env.REACT_APP_BYPASS_JWT === 'true') {
          // Use environment variables for testing
          console.log('🧪 JWT bypass enabled - using environment variables for testing');
          const testRole = process.env.REACT_APP_TEST_ROLE as 'STAFF' | 'resident' || 'resident';
          jwtPayload = {
            residentId: process.env.REACT_APP_TEST_RESIDENT_LOCATION_ID || '287743',
            facilityId: process.env.REACT_APP_TEST_FACILITY_ID || 'facility_123',
            externalId: process.env.REACT_APP_TEST_EXTERNAL_ID || process.env.REACT_APP_TEST_RESIDENT_LOCATION_ID || '287743',
            role: testRole,
            iat: Math.floor(Date.now() / 1000),
            exp: Math.floor(Date.now() / 1000) + (4 * 60 * 60) // 4 hours
          };
          
          console.log('Test payload:', {
            residentId: jwtPayload.residentId,
            facilityId: jwtPayload.facilityId,
            role: jwtPayload.role,
            note: 'residentId = locationId in CareDaily API'
          });
        } else {
          // 1. Ensure JWT service is initialized first
          console.log('🔐 Ensuring JWT service is initialized...');
          try {
            await JWTService.initialize();
          } catch (initError) {
            console.error('JWT service initialization failed:', initError);
            // Continue anyway - might be in bypass mode
          }

          // 2. Get and verify JWT token with RSA signature (production mode)
          const token = JWTService.getTokenFromQuery();
          if (!token) {
            throw new Error('No authentication token found. Please ensure the dashboard is properly embedded with a valid JWT token.');
          }

          jwtPayload = await JWTService.processToken(token);
          if (!jwtPayload) {
            throw new Error('Authentication failed. Invalid JWT token or signature verification failed.');
          }

          // Store token for future use
          JWTService.storeToken(token);
        }
      }

      if (!jwtPayload) {
        throw new Error('JWT payload is null after processing');
      }

      // ============ TEMPORARY DEBUG LOGGING ============
      console.log('🔍 JWT Payload Keys:', Object.keys(jwtPayload));
      console.log('🔍 apiKey present:', !!jwtPayload.apiKey);
      console.log('🔍 role:', jwtPayload.role);
      console.log('🔍 facilityId:', jwtPayload.facilityId);
      console.log('🔍 residentId present:', !!jwtPayload.residentId);
      console.log('🔍 externalId present:', !!jwtPayload.externalId);
      // ================================================

      // For STAFF users, use the selected resident ID instead of the JWT's resident ID.
      // Fall back to 'SAMPLE_DATA' if selectedResidentId is empty — this happens when Dashboard
      // first mounts before the JWT payload has loaded (App.tsx renders Dashboard with jwtPayload=null,
      // so the useState initializer can't see the role and defaults to '').
      const effectiveResidentId = isStaff
        ? (selectedResidentId || 'SAMPLE_DATA')
        : jwtPayload.residentId;
      
      // Check if using sample data (debug overrides bypass sample data).
      // Demo mode always uses sample data — it promises zero API calls.
      const usingSampleData = isDemoMode() ||
        (effectiveResidentId === 'SAMPLE_DATA' && !debugLocationOverride.trim() && !debugExternalIdOverride.trim());
      
      console.log(`👤 Loading dashboard for ${usingSampleData ? 'SAMPLE DATA' : 'resident: ' + effectiveResidentId}${isStaff ? ' (STAFF viewing)' : ''}`);

      // Create a payload with the effective resident ID for API calls
      const effectivePayload = {
        ...jwtPayload,
        residentId: usingSampleData ? jwtPayload.residentId : effectiveResidentId
      };

      // 2. Get metric configurations, categories, dimensions, and program settings
      // Ensure facilityId is a string for all service calls
      const facilityIdString = jwtPayload.facilityId ? String(jwtPayload.facilityId).trim() : '';
      let metricConfigs: MetricConfig[];
      let categories: DashboardCategory[];
      let dimensions: WellnessDimension[];
      let programSettings: ProgramSettings;
      if (isDemoMode()) {
        // Offline facility config — Supabase (correctly) denies unauthenticated
        // reads, and demo mode promises no API connection at all.
        const Demo = await import('../services/demoDataService');
        metricConfigs = Demo.getDemoMetricConfigs();
        categories = Demo.getDemoCategories();
        dimensions = Demo.getDemoDimensions();
        programSettings = Demo.getDemoProgramSettings();
      } else {
        [metricConfigs, categories, dimensions, programSettings] = await Promise.all([
          MetricConfigService.getEffectiveMetricConfigs(facilityIdString),
          CategoryService.getCategories(facilityIdString),
          DimensionService.getDimensions(facilityIdString),
          ProgramSettingsService.getProgramSettings(facilityIdString)
        ]);

        // Non-blocking: help dialogs fall back to their built-in defaults while this
        // is still loading (or if it fails), so it isn't part of the Promise.all above.
        // Demo mode skips the fetch entirely — the defaults ARE the demo content.
        ResidentHelpContentService.getContent(facilityIdString).then(setHelpContent);
      }

      // 3. Fetch wellness data - use demo data if SAMPLE_DATA is selected
      let wellnessData: any;
      let careDailyConnectionError: string | null = null;
      let selectedLocationId: string | null = null; // Declare at higher scope for ActivityLogger
      
      if (usingSampleData) {
        console.log('📊 Using SAMPLE DATA (demo mode)');
        const DemoDataService = await import('../services/demoDataService');
        const mockWellnessData = DemoDataService.getMockWellnessData();
        const mockTimeSeriesData = DemoDataService.getMockTimeSeriesData(90); // 90 days of data
        
        console.log('🔍 Mock wellness data:', mockWellnessData);
        console.log('🔍 Mock time series data:', mockTimeSeriesData);
        
        // Parse the mock data the same way we would parse real data
        const careDailyService = createCareDailyServiceForDemo();
        // parseWellnessData expects the CareDaily API envelope (currentState.states[]);
        // the mock is a flat {stateName: state} object, so wrap it — without this the
        // whole sample path parses to empty metrics.
        const parsedData = careDailyService.parseWellnessData({
          currentState: { states: Object.values(mockWellnessData as Record<string, any>) },
        });
        const mappedMetrics = careDailyService.mapMetricsForDashboard(parsedData);
        const timeSeriesData = mockTimeSeriesData; // Already in the right format
        
        console.log('✅ Parsed demo data - mappedMetrics:', mappedMetrics);
        console.log('✅ Parsed demo data - timeSeriesData keys:', Object.keys(timeSeriesData));
        
        wellnessData = { mappedMetrics, timeSeriesData };

        if (isDemoMode()) {
          // The demo dimensions score off recentTrends via the legacy path
          // (dimensionMetrics is empty), so expose the parsed mock trends the
          // same way the live path exposes CareDaily's — this is what lights
          // up the wellness pie with real relative percentages.
          (wellnessData as any)._rawData = {
            rawRecentTrends: parsedData.recentTrends,
            metadata: parsedData.metadata,
          };
        }
      } else {
        try {
        // Fetch from CareDaily API (pass facilityId to load credentials from database)
        const careDailyService = await createCareDailyService(facilityIdString);
          // Priority: debug panel override > URL override (?location=ID) > lookup by externalId > residentId from JWT
          //
          // Both overrides are staff-only. The ?location= parameter used to apply
          // unconditionally, so any resident could read another resident's
          // MoCA/PHQ-9/mobility data just by editing the URL — location ids are
          // short sequential integers. The debug-panel override was already
          // gated on isStaff at the UI level; this makes the gate real for both.
          const isStaffUser = effectivePayload.role?.toUpperCase() === 'STAFF';
          const urlParams = new URLSearchParams(window.location.search);
          const requestedLocationId = debugLocationOverride.trim() || urlParams.get('location') || undefined;
          if (requestedLocationId && !isStaffUser) {
            console.warn('⛔ Ignoring location override for non-staff user');
          }
          const overrideLocationId = isStaffUser ? requestedLocationId : undefined;
          const overrideExternalId = isStaffUser ? (debugExternalIdOverride.trim() || undefined) : undefined;
          
          if (overrideLocationId) {
            selectedLocationId = overrideLocationId;
            console.log('📍 Using location override:', selectedLocationId, debugLocationOverride ? '(debug panel)' : '(URL param)');
            if (overrideExternalId) {
              console.log('🪪 Using external ID override:', overrideExternalId, '(debug panel)');
            }
          } else {
            // Determine the externalUserId to use for the CareDaily lookup:
            // - STAFF: selectedResidentId (effectiveResidentId) is the externalUserId from GoIcon
            // - resident: JWT externalId is the externalUserId (NOT the CareDaily locationId)
            const externalUserIdForLookup =
              effectivePayload.role?.toUpperCase() === 'STAFF'
                ? effectiveResidentId
                : (jwtPayload.externalId || effectiveResidentId);

            // Look up CareDaily location by externalUserId.
            const userType = effectivePayload.role?.toUpperCase() === 'STAFF' ? 'Staff' : 'Resident';
            console.log(`👥 ${userType} user - looking up CareDaily location by externalUserId:`, externalUserIdForLookup);
              console.log('🔧 Using program settings:', {
                externalAppId: programSettings.externalAppId,
                defaultExternalRole: programSettings.defaultExternalRole
              });
              
              if (!externalUserIdForLookup) {
                throw new Error('NO_RESIDENT_SELECTED');
              }

              try {
                const userLookup = await careDailyService.getUserByExternalId(
                  externalUserIdForLookup,
                  programSettings.externalAppId,
                  programSettings.defaultExternalRole
                );
                selectedLocationId = userLookup.locationId; // This is the CareDaily locationId
                console.log(`✅ Found CareDaily locationId: ${selectedLocationId} for externalUserId: ${externalUserIdForLookup}`);
                console.log('📌 This locationId will be stored and used for all CareDaily API calls');
                
              } catch (lookupError) {
                console.error('❌ Failed to lookup user by externalUserId:', externalUserIdForLookup, lookupError);
                // Distinguish a genuine "not enrolled" from a transient/technical
                // failure. getUserByExternalId throws its own "No … found" messages
                // only when CareDaily actually has no such user/location; anything
                // else (timeout, 5xx, auth, network) is a hiccup that usually clears
                // on retry and must not be shown to staff as an enrollment problem.
                const lookupMsg = lookupError instanceof Error ? lookupError.message : String(lookupError);
                const genuinelyNotEnrolled = /^No\b.*found|not found in CareDaily/i.test(lookupMsg);
                if (genuinelyNotEnrolled) {
                  throw new Error(`RESIDENT_NOT_IN_CAREDAILY:${externalUserIdForLookup}`);
                }
                throw new Error(`CAREDAILY_LOOKUP_FAILED:${lookupMsg}`);
              }
          }

          if (!selectedLocationId) {
            throw new Error('No location ID available');
          }

          console.log('📍 Fetching wellness data for location:', selectedLocationId);
          if (effectivePayload.externalId) {
            console.log('   External ID (for reference):', effectivePayload.externalId);
          }

          const rawWellnessResponse = await careDailyService.getResidentWellnessDataByLocation(selectedLocationId);
          wellnessData = rawWellnessResponse;

            // Also fetch time-series data for charts (using default 12-month range for now)
            if (process.env.NODE_ENV !== 'production') {
              console.log('📈 Fetching time-series data for charts (location:', selectedLocationId, ')');
            }
            let timeSeriesResponse;
            try {
              // Fetch both trends and wellness_assessment time series
              timeSeriesResponse = await careDailyService.getCombinedTimeSeries(selectedLocationId);
              if (process.env.NODE_ENV !== 'production') {
                console.log('✅ Time-series response received. States count:', timeSeriesResponse?.states?.length || 0);
              }
            } catch (timeSeriesError) {
              console.error('❌ Time-series fetch failed:', timeSeriesError);
              // timeSeriesResponse stays undefined; parseTimeSeriesData handles it gracefully
            }

          // Capture raw responses for staff debug panel
          setDebugRawResponses({
            locationId: selectedLocationId,
            wellnessProfile: rawWellnessResponse,
            timeSeries: timeSeriesResponse ?? null,
          });

          // Best-effort: fetch goals + activities for staff debug panel
          if (isStaff && selectedLocationId) {
            (async () => {
              const debugExtras: Record<string, any> = {};

              // Goals state from CareDaily
              try {
                const goalsRaw = await careDailyService.makeRequest<any>(
                  `/locations/${selectedLocationId}/state`,
                  'GET',
                  { name: 'goals' }
                );
                debugExtras.goalsState = goalsRaw;
              } catch (e) {
                debugExtras.goalsState = { error: e instanceof Error ? e.message : String(e) };
              }


              setDebugRawResponses(prev => ({ ...prev, ...debugExtras }));
            })();
          }

          const parsedData = careDailyService.parseWellnessData(wellnessData);

          // Build per-metric value-key overrides so parseTimeSeriesData uses the same
          // configured sub-field (e.g. 'avg', 'value') as the current-state cards.
          const valueKeyByMetric: Record<string, string> = {};
          for (const mc of metricConfigs) {
            if (mc.careDailyValueKey) valueKeyByMetric[mc.metricKey] = mc.careDailyValueKey;
          }

          // Parse time-series data for charts
          const timeSeriesData = careDailyService.parseTimeSeriesData(timeSeriesResponse, valueKeyByMetric);

          // Extract the most recent observed value per metric from the time series.
          // This is the correct current value for DOW metrics — trends_recently only has
          // rolling averages (avg/std/n) with no display string or zscore.
          const latestFromTimeSeries = careDailyService.extractLatestFromTimeSeries(timeSeriesResponse);

          // Map metrics, passing the time series latest so DOW cards show actual values
          const mappedMetrics = careDailyService.mapMetricsForDashboard(parsedData, latestFromTimeSeries);

          // The wheel scores off the last 30 days of the trends time series (including
          // today). If the time series fetch failed, it falls back to trends_recently.
          if (timeSeriesResponse) {
            mappedMetrics._rawData.wheelTrends = buildWheelTrends(timeSeriesResponse.states);
          }

          wellnessData = { mappedMetrics, timeSeriesData };
        } catch (apiError) {
          // CareDaily API connection failed - continue with empty data and show error message
          console.error('⚠️ CareDaily API connection failed:', apiError);
          
          const errorMessage = apiError instanceof Error ? apiError.message : 'Unable to connect to CareDaily API';

          if (errorMessage === 'NO_RESIDENT_SELECTED') {
            careDailyConnectionError = 'NO_RESIDENT_SELECTED';
            console.warn('⚠️ No resident selected');
          } else if (errorMessage.startsWith('RESIDENT_NOT_IN_CAREDAILY:')) {
            careDailyConnectionError = 'RESIDENT_NOT_IN_CAREDAILY';
            console.warn('⚠️ Resident not found in CareDaily:', errorMessage);
          } else if (errorMessage.startsWith('CAREDAILY_LOOKUP_FAILED')) {
            // Transient/technical lookup failure — not an enrollment problem.
            careDailyConnectionError = 'CAREDAILY_TRANSIENT';
            console.warn('⚠️ Transient CareDaily lookup failure (retryable):', errorMessage);
          } else if (errorMessage.includes('ACCESS_DENIED') || errorMessage.includes('wrong place')) {
            careDailyConnectionError = 'ACCESS_DENIED';
            console.warn('⚠️ Access denied error');
          } else if (errorMessage.includes('No CareDaily API key') || errorMessage.includes('Missing facilityId')) {
            careDailyConnectionError = 'NO_API_KEY_STAFF';
            console.warn('⚠️ No CareDaily API key configured for facility');
          } else {
            careDailyConnectionError = errorMessage;
          }
          
          // Use empty data
          wellnessData = {
            mappedMetrics: {},
            timeSeriesData: {}
          };
        }
      }

      const { mappedMetrics, timeSeriesData } = wellnessData;

      console.log('📊 Setting dashboard state with:');
      console.log('- Mapped metrics:', mappedMetrics);
      console.log('- Time-series data:', timeSeriesData);
      console.log('- Metric configs:', metricConfigs);
      console.log('- Metric config keys:', metricConfigs.map(c => c.metricKey));
      console.log('- Available data keys:', Object.keys(mappedMetrics));
      
      if (requestId !== initializeRequestIdRef.current) {
        return;
      }
      setState(prev => ({
        ...prev,
        loading: false,
        careDailyConnectionError,
        jwtPayload: effectivePayload,
        wellnessData: mappedMetrics,
        timeSeriesData,
        metricConfigs,
        categories,
        dimensions,
        programName: programSettings.programName,
        primaryColor: programSettings.primaryColor,
        secondaryColor: programSettings.secondaryColor,
        programSettings: programSettings,
        // Store current resident IDs for ActivityLogger
        currentLocationId: !usingSampleData ? selectedLocationId : null,
        currentExternalId: !usingSampleData
          ? (debugExternalIdOverride.trim() || (effectivePayload.role === 'STAFF'
              ? effectiveResidentId
              : (effectivePayload.externalId || effectiveResidentId)))
          : null
      }));

      // --- Self-assessment loading (runs after main state is set) ---
      // Only relevant when CareDaily is NOT connected
      if (careDailyConnectionError && !usingSampleData) {
        // These enrichment fetches run after the main render and each await one
        // another. If the user switches resident mid-flight, a newer
        // initializeDashboard has already bumped the request id; committing this
        // (now stale) resident's results would cross-contaminate the new view.
        // Re-check before every state commit, matching the main flow's guard.
        const isStale = () => requestId !== initializeRequestIdRef.current;
        try {
          const saConfig = await SelfAssessmentConfigService.getConfig(facilityIdString);
          if (isStale()) return;
          setSelfAssessmentConfig(saConfig);

          if (saConfig && saConfig.enabled) {
            const isDue = await SelfAssessmentResponseService.isDue(
              facilityIdString,
              effectiveResidentId,
              saConfig.frequency
            );
            if (isStale()) return;
            setSelfAssessmentDue(isDue);

            // Load latest self-reported values so metric cards can display them
            const latestResponses = await SelfAssessmentResponseService.getLatestResponses(
              facilityIdString,
              effectiveResidentId
            );
            if (isStale()) return;
            setLatestSelfAssessmentData(latestResponses);

            // Build mappedMetrics from self-assessment responses
            const selfReportedMetrics: Record<string, any> = {};
            for (const mc of metricConfigs) {
              const resp = latestResponses[mc.id];
              if (resp) {
                selfReportedMetrics[mc.metricKey] = {
                  value: resp.value,
                  avg: resp.value,
                  display: `${resp.value}`,
                  updated_ms: new Date(resp.respondedAt).getTime(),
                  std: 0,
                  zscore: 0
                };
              }
            }

            if (Object.keys(selfReportedMetrics).length > 0) {
              if (isStale()) return;
              setState(prev => ({
                ...prev,
                wellnessData: { ...prev.wellnessData, ...selfReportedMetrics }
              }));
            }
          }
        } catch (saError) {
          console.warn('Self-assessment loading failed (non-fatal):', saError);
        }

        // Fetch resident's schedule from GoIcon calendar-central for pie chart dimension breakdown
        // Uses the auth-scoped schedule endpoint (returns only the active resident's events)
        try {
          const apiKey = jwtPayload.apiKey;
          if (apiKey && facilityIdString && !usingSampleData) {
            const activitiesService = new GoIconActivitiesService({
              facilityId: facilityIdString,
              apiKey
            });
            const now = Math.floor(Date.now() / 1000);
            const ninetyDaysAgo = now - 90 * 24 * 60 * 60;
            const scheduleEvents = await activitiesService.getResidentSchedule(ninetyDaysAgo, now);
            if (isStale()) return;

            // Build GoIcon dimension ID -> wellness dimension ID map
            const goiconToDimId: Record<number, string> = {};
            for (const dim of dimensions) {
              if (dim.goiconDimensionId) {
                goiconToDimId[dim.goiconDimensionId] = dim.id;
              }
            }

            const dimCounts = GoIconActivitiesService.extractDimensionCountsFromSchedule(
              scheduleEvents,
              goiconToDimId
            );

            // Normalize to 100%
            const total = Object.values(dimCounts).reduce((s, v) => s + v, 0);
            const pcts: Record<string, number> = {};
            if (total > 0) {
              for (const [dimId, count] of Object.entries(dimCounts)) {
                pcts[dimId] = Math.round((count / total) * 1000) / 10;
              }
            }
            setGoiconDimensionPcts(pcts);
          }
        } catch (goiconError) {
          console.warn('GoIcon schedule fallback for pie chart failed (non-fatal):', goiconError);
        }
      }

    } catch (error) {
      console.error('Dashboard initialization error:', error);
      if (requestId !== initializeRequestIdRef.current) {
        return;
      }
      setState(prev => ({
        ...prev,
        loading: false,
        error: error instanceof Error ? error.message : 'An unexpected error occurred'
      }));
    }
  }, [initialJwtPayload, isStaff, selectedResidentId, debugLocationOverride, debugExternalIdOverride]);

  // Initialize dashboard data
  useEffect(() => {
    initializeDashboard();
  }, [initializeDashboard]);

  // Resident user flow: resolve GoIcon internal user id.
  // Staff flow sets goiconResidentId via ResidentSelector.
  useEffect(() => {
    const payload = state.jwtPayload;
    if (!payload) return;
    if (isStaff) return;
    if (goiconResidentId) return;
    if (!payload.apiKey || !payload.facilityId) return;

    console.log('🔎 Resolving GoIcon resident ID for resident user:', {
      residentId: payload.residentId || '(empty)',
      externalId: payload.externalId || '(empty)',
      facilityId: payload.facilityId,
    });

    const resolve = async () => {
      const externalId = payload.externalId;

      // Try lookup by externalId against residents list
      if (externalId) {
        try {
          const svc = createGoIconApiService(payload.apiKey);
          const resp = await svc.getFacilityResidents(String(payload.facilityId).trim());

          const arr = Array.isArray(resp)
            ? resp
            : (Array.isArray(resp?.residents) ? resp.residents : (Array.isArray(resp?.data) ? resp.data : []));

          console.log('🔎 Residents list for GoIcon ID lookup:', arr.length, 'residents');

          const match = arr.find((r: any) => String(r?.v_external_id ?? r?.external_id ?? r?.externalId ?? '').trim() === String(externalId).trim());
          if (match?.id != null) {
            setGoiconResidentId(String(match.id));
            console.log('✅ Resolved GoIcon resident id via externalId lookup:', String(match.id));
            return;
          }
          console.warn('⚠️ No externalId match found, trying residentId fallback');
        } catch (e) {
          console.warn('⚠️ Residents list fetch failed, trying residentId fallback:', e);
        }
      }

      // Fallback: use residentId from JWT directly (this is often the GoIcon internal ID)
      if (payload.residentId) {
        console.log('✅ Using JWT residentId as GoIcon resident id (fallback):', payload.residentId);
        setGoiconResidentId(String(payload.residentId));
      } else {
        console.error('❌ Cannot resolve GoIcon resident ID — no residentId or externalId in JWT');
      }
    };

    resolve();
  }, [state.jwtPayload, isStaff, goiconResidentId]);

  // Apply custom brand colors as CSS variables
  useEffect(() => {
    document.documentElement.style.setProperty('--brand-primary', state.primaryColor);
    document.documentElement.style.setProperty('--brand-secondary', state.secondaryColor);
    document.documentElement.style.setProperty(
      '--brand-gradient', 
      `linear-gradient(135deg, ${state.primaryColor} 0%, ${state.secondaryColor} 100%)`
    );
  }, [state.primaryColor, state.secondaryColor]);

  const handleTabChange = (tabId: string) => {
    setState(prev => ({ ...prev, activeTab: tabId }));
  };

  const getMetricsForTab = (tabId: string): MetricConfig[] => {
    debugLog(`🔍 Getting metrics for tab: ${tabId}`);
    debugLog(`📊 All metric configs:`, state.metricConfigs.map(c => ({
      key: c.metricKey,
      categoryId: c.categoryId,
      tab: c.tab, // Legacy
      visible: c.isVisible
    })));

    // Filter metrics by categoryId (UUID reference)
    // Fall back to legacy tab field for backward compatibility during migration
    const tabMetrics = state.metricConfigs.filter(config =>
      config.categoryId === tabId || config.tab === tabId
    ).sort((a, b) => a.sortOrder - b.sortOrder);

    debugLog(`✅ Found ${tabMetrics.length} metrics for tab ${tabId}:`, tabMetrics.map(c => c.metricKey));
    return tabMetrics;
  };

  /** Sections for the "What do these scores mean?" dialog, for the page it was opened
   *  from. The overview tab has no metric cards, so it keeps the facility-wide text
   *  alone; every other tab leads with the explanation of the scores on screen — the
   *  admin help text, or CareDaily's own metric comment where that is blank, which is
   *  the same precedence the card itself uses. Composition lives in
   *  buildScoreInfoSections so it is unit-testable. */
  const getScoreInfoSections = (): HelpSection[] => {
    const facilitySections = helpContent?.scoreInfo ?? DEFAULT_SCORE_INFO;
    if (state.activeTab === 'overview') return facilitySections;

    const metrics = getMetricsForTab(state.activeTab).map(config => ({
      metricKey: config.metricKey,
      displayName: config.displayName,
      helpText: config.helpText,
      fallbackText: getMetricDataByKey(
        config.metricKey,
        config.careDailyValueKey,
        config.careDailyDisplayKey
      )?.metadata?.comment,
      isVisible: config.isVisible,
    }));

    return buildScoreInfoSections(metrics, facilitySections);
  };

  // Removed unused toggleCategory and handleSearch

  const handleDateRangeChange = (days: number, label: string) => {
    setDateRange({ days, label });
    console.log(`📅 Changed date range to: ${label} (${days} days)`);
  };

  const renderDateRangeSelector = () => {
    const dateRangeOptions = [
      { days: 90, label: 'Last 3 Months' },
      { days: 180, label: 'Last 6 Months' },
      { days: 365, label: 'Last 1 Year' },
      { days: 730, label: 'Last 2 Years' },
      { days: 36500, label: 'All Time' }
    ];

    return (
      <div className="date-range-selector">
        <div 
          className="date-range-header"
          onClick={() => setShowDateRange(!showDateRange)}
        >
          <div className="date-range-header-content">
            <span className="date-range-icon" aria-hidden="true">
              <Icon name="FaRegCalendarAlt" size={22} />
            </span>
            <div className="date-range-header-text">
              <span className="date-range-label">Time Period</span>
              <span className="date-range-current">{dateRange.label}</span>
            </div>
          </div>
          <button
            type="button"
            className={`date-range-toggle ${showDateRange ? 'expanded' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              setShowDateRange(!showDateRange);
            }}
            aria-label={showDateRange ? 'Collapse time period' : 'Expand time period'}
          >
            {showDateRange ? (
              <svg className="toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"></polyline>
              </svg>
            ) : (
              <svg className="toggle-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 18 15 12 9 6"></polyline>
              </svg>
            )}
          </button>
        </div>
        
        {showDateRange && (
          <div className="date-range-buttons">
            {dateRangeOptions.map(option => (
              <button
                key={option.days}
                className={`date-range-button ${dateRange.days === option.days ? 'active' : ''}`}
                onClick={() => handleDateRangeChange(option.days, option.label)}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  // Removed getOtherMetricsConfigs - using database-configured metrics only

  // Removed unused formatCategoryName and formatMetricName

  // Wellness message function - will be re-enabled when overall wellness card is shown
  // const getWellnessMessage = (score: number, zscore?: number): string => {
  //   // Personalized messages based on actual score and trend
  //   const trendText = zscore !== undefined ? 
  //     (zscore > 0.5 ? ' and improving! 📈' : 
  //      zscore < -0.5 ? ', let\'s work on this together 💪' : 
  //      ' and staying steady 📊') : '';

  //   if (score >= 80) {
  //     return `Outstanding wellness! You're doing amazing${trendText}`;
  //   } else if (score >= 70) {
  //     return `Great wellness indicators! Keep up the good work${trendText}`;
  //   } else if (score >= 60) {
  //     return `Good progress! You're on the right track${trendText}`;
  //   } else if (score >= 50) {
  //     return `Making progress! Small steps lead to big improvements${trendText}`;
  //   } else {
  //     return `Every day is a new opportunity to feel better${trendText}`;
  //   }
  // };

  // After an activity is logged, CareDaily needs a few seconds to fold it into the
  // trends time series. There is no websocket for time series states, so CareDaily
  // (Destry, 2026-10-23) suggested refreshing the data after 3–5 seconds.
  const handleActivitySubmit = (activity: string, dimensions: string[]) => {
    debugLog('Activity logged:', { activity, dimensions });
    const locationId = state.currentLocationId;
    const facilityId = state.jwtPayload?.facilityId ? String(state.jwtPayload.facilityId).trim() : '';
    if (!locationId || !facilityId || isDemoMode()) return;

    const requestId = initializeRequestIdRef.current;
    if (wheelRefreshTimerRef.current) clearTimeout(wheelRefreshTimerRef.current);
    wheelRefreshTimerRef.current = setTimeout(async () => {
      wheelRefreshTimerRef.current = null;
      try {
        const careDailyService = await createCareDailyService(facilityId);
        const timeSeries = await careDailyService.getTimeSeriesData(locationId);
        // Drop the result if the dashboard has since reloaded or switched resident.
        if (requestId !== initializeRequestIdRef.current) return;
        const wheelTrends = buildWheelTrends(timeSeries?.states);
        setState(prev => prev.wellnessData?._rawData
          ? {
              ...prev,
              wellnessData: {
                ...prev.wellnessData,
                _rawData: { ...prev.wellnessData._rawData, wheelTrends }
              }
            }
          : prev);
      } catch (error) {
        console.error('Failed to refresh the wellness wheel after logging an activity:', error);
      }
    }, WHEEL_REFRESH_DELAY_MS);
  };

  // Helper function to prepare dimensions with percentages for charts
  const prepareDimensionsWithPercentages = () => {
    // Score each DOW dimension on the 30-day average from the trends time series
    // (see utils/wheelTrends). Falls back to trends_recently (7 days, excluding
    // today) when the time series wasn't available, e.g. in demo mode.
    const rawData = (state.wellnessData as any)?._rawData;
    const recentTrends = rawData?.wheelTrends || rawData?.rawRecentTrends || rawData?.recentTrends || {};
    const metadata = rawData?.metadata || {}; // trends_metadata (max_value for the 'apiMax' mode)

    // Facility-level sub-metric list + the category roots of all active dimensions
    // (the denominator set for the "share across categories" scoring).
    const subMetrics = state.programSettings?.dimensionMetrics ?? [];
    const allCategoryRoots = state.dimensions
      .filter(d => d.isActive && d.metricKey)
      .map(d => d.metricKey as string);

    debugLog('🥧 Preparing dimensions with percentages from wellness data');
    debugLog('🥧 Available recent trends:', Object.keys(recentTrends));

    // First pass: collect all dimension scores (share-across-categories).
    const dimensionsWithScores = state.dimensions.map((dim) => {
      const category = dim.categoryId
        ? state.categories.find(cat => cat.id === dim.categoryId)
        : undefined;

      const score = computeDimensionScore(dim.metricKey, dim.valueKey, subMetrics, allCategoryRoots, recentTrends, metadata);
      debugLog(`🥧 Dimension "${dim.name}": score = ${score}`);

      return {
        ...dim,
        categoryId: dim.categoryId || undefined,
        categoryName: category?.displayName,
        categoryIcon: category?.icon,
        categoryColor: category?.color,
        categorySortOrder: category?.sortOrder || 0,
        score: score ?? 0,
        hasData: score !== null
      };
    });

    // Total over dimensions that actually have data, so no-data dims
    // don't drag real percentages down (they were already contributing 0).
    const totalScore = dimensionsWithScores
      .filter(d => d.hasData)
      .reduce((sum, dim) => sum + dim.score, 0);
    debugLog(`🥧 Total score across all dimensions: ${totalScore}`);

    // Second pass: convert scores to percentages
    return dimensionsWithScores.map((dim) => {
      const percentage = totalScore > 0 ? (dim.score / totalScore) * 100 : 0;
      debugLog(`🥧 Dimension "${dim.name}": ${dim.score} / ${totalScore} = ${percentage.toFixed(1)}%`);
      
      return {
        ...dim,
        percentage: Math.round(percentage * 10) / 10 // Round to 1 decimal place
      };
    });
  };

  // Recomputed independently by renderProgressLeader, renderWellnessMixChart, and
  // buildResidentNeedFromDimensionScores on every render before this memo; the
  // dependency list covers everything prepareDimensionsWithPercentages reads:
  // state.wellnessData (rawData/recentTrends), state.programSettings?.dimensionMetrics,
  // state.dimensions, and state.categories. (goiconDimensionPcts is not read inside it.)
  const dimensionsWithPercentages = useMemo(
    () => prepareDimensionsWithPercentages(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.wellnessData, state.dimensions, state.categories, state.programSettings]
  );

  const buildResidentNeedFromDimensionScores = (): ResidentNeed => {
    const dims = dimensionsWithPercentages;

    const byKey: Record<RecWellnessDimension, number[]> = {
      mind: [],
      body: [],
      social: [],
      spirit: [],
      environment: [],
      purpose: [],
      financial: [],
      other: [],
    };

    const normalizeScore = (score: unknown) => {
      const n = Number(score);
      if (!Number.isFinite(n)) return null;
      return Math.max(0, Math.min(100, n));
    };

    const mapCategoryKey = (key?: string | null): RecWellnessDimension => {
      const k = (key || '').toLowerCase().trim();
      const allowed: RecWellnessDimension[] = ['mind','body','social','spirit','environment','purpose','financial','other'];
      return (allowed as string[]).includes(k) ? (k as RecWellnessDimension) : 'other';
    };

    // If dimension is categorized, use that category key. Otherwise map by dimension name.
    // Skip no-data dims — otherwise they'd read as a genuine score of 0 and wrongly
    // rank as the resident's greatest need.
    for (const d of (dims as any[]).filter(d => d.hasData !== false)) {
      const score = normalizeScore(d.score);
      if (score === null) continue;

      let key: RecWellnessDimension = 'other';
      if (d.categoryId) {
        const cat = state.categories.find(c => c.id === d.categoryId);
        key = mapCategoryKey(cat?.categoryKey);
      } else {
        console.warn(`⚠️ Dimension "${d.name}" has no categoryId — bucketed as "other" in need vector`);
      }

      byKey[key].push(score);
    }

    const avg = (arr: number[]) => arr.reduce((s, x) => s + x, 0) / arr.length;
    const toNeed = (avgScore: number) => Math.max(0, Math.min(1, (100 - avgScore) / 100));

    const need: Record<RecWellnessDimension, number> = {
      mind: byKey.mind.length ? toNeed(avg(byKey.mind)) : 0,
      body: byKey.body.length ? toNeed(avg(byKey.body)) : 0,
      social: byKey.social.length ? toNeed(avg(byKey.social)) : 0,
      spirit: byKey.spirit.length ? toNeed(avg(byKey.spirit)) : 0,
      environment: byKey.environment.length ? toNeed(avg(byKey.environment)) : 0,
      purpose: byKey.purpose.length ? toNeed(avg(byKey.purpose)) : 0,
      financial: byKey.financial.length ? toNeed(avg(byKey.financial)) : 0,
      other: byKey.other.length ? toNeed(avg(byKey.other)) : 0,
    };

    return { need };
  };

  const buildGoIconDimensionIdToWellnessMap = (): Record<number, RecWellnessDimension> => {
    const allowed: RecWellnessDimension[] = ['mind','body','social','spirit','environment','purpose','financial','other'];
    const normalizeKey = (key?: string | null): RecWellnessDimension => {
      const k = (key || '').toLowerCase().trim();
      return (allowed as string[]).includes(k) ? (k as RecWellnessDimension) : 'other';
    };

    const map: Record<number, RecWellnessDimension> = {};
    for (const dim of state.dimensions) {
      const id = dim.goiconDimensionId;
      if (!Number.isFinite(id as any)) continue;

      // Prefer category UUID -> categoryKey; fallback to name-based mapping.
      let categoryKey: string | undefined = undefined;
      if (dim.categoryId) {
        const cat = state.categories.find(c => c.id === dim.categoryId);
        categoryKey = cat?.categoryKey;
      }
      if (!categoryKey && typeof dim.name === 'string') {
        const n = dim.name.toLowerCase();
        if (n.includes('physical') || n.includes('nutri')) categoryKey = 'body';
        else if (n.includes('social')) categoryKey = 'social';
        else if (n.includes('spirit')) categoryKey = 'spirit';
        else if (n.includes('environment')) categoryKey = 'environment';
        else if (n.includes('purpose') || n.includes('vocation')) categoryKey = 'purpose';
        else if (n.includes('financial')) categoryKey = 'financial';
        else if (n.includes('emotional') || n.includes('intellectual') || n.includes('cognitive') || n.includes('mind')) categoryKey = 'mind';
      }

      map[Number(id)] = normalizeKey(categoryKey);
    }
    return map;
  };

  const buildGoIconDimensionIdToDrivenByLabelMap = (): Record<number, string> => {
    const map: Record<number, string> = {};
    for (const dim of state.dimensions) {
      const id = dim.goiconDimensionId;
      if (!Number.isFinite(id as any)) continue;

      // If the dimension is assigned to a dashboard category, prefer the category display name.
      if (dim.categoryId) {
        const cat = state.categories.find(c => c.id === dim.categoryId);
        if (cat?.displayName) {
          map[Number(id)] = cat.displayName;
          continue;
        }
      }

      if (typeof dim.name === 'string' && dim.name.trim()) {
        map[Number(id)] = dim.name.trim();
      }
    }
    return map;
  };

  // Header removed per user request

  const renderProgressLeader = () => {
    if (!state.jwtPayload) return null;
    
    // Check if progress leader is enabled in program settings
    if (state.programSettings && state.programSettings.showProgressLeader === false) {
      return null;
    }
    
    // Don't show progress leader if there's a connection error
    if (state.careDailyConnectionError) {
      return null;
    }
    
    // Check if we're using sample/demo data
    const usingSampleData = selectedResidentId === 'SAMPLE_DATA';
    
    // Only show progress leader if there's actual wellness data OR using demo
    const hasWellnessData = state.wellnessData && 
      typeof state.wellnessData === 'object' && 
      Object.keys(state.wellnessData).length > 0 &&
      Object.keys(state.wellnessData).some(key => key !== '_rawData');
    
    if (!hasWellnessData && !usingSampleData) {
      console.log('📊 No wellness data available - skipping progress leader');
      return null;
    }
    
    return (
      <ProgressLeader
        dimensions={dimensionsWithPercentages.filter(d => d.hasData !== false)}
        facilityId={state.jwtPayload.facilityId ? String(state.jwtPayload.facilityId).trim() : ''}
        primaryColor={state.primaryColor}
        secondaryColor={state.secondaryColor}
      />
    );
  };

  const renderWellnessMixChart = () => {
    // Check if we're using sample/demo data
    const usingSampleData = selectedResidentId === 'SAMPLE_DATA';
    
    // Check if we have actual wellness data
    const hasWellnessData = state.wellnessData && 
      typeof state.wellnessData === 'object' && 
      Object.keys(state.wellnessData).length > 0 &&
      Object.keys(state.wellnessData).some(key => key !== '_rawData');
    
    // Prepare dimensions with appropriate percentages from wellness data
    let enrichedDimensions;
    if (hasWellnessData || usingSampleData) {
      // Has data OR is demo mode - get actual percentages from CareDaily metrics
      enrichedDimensions = dimensionsWithPercentages;
    } else if (Object.keys(goiconDimensionPcts).length > 0) {
      // No CareDaily data -- use GoIcon activity attendance percentages
      enrichedDimensions = state.dimensions.map(dim => {
        const category = dim.categoryId
          ? state.categories.find(cat => cat.id === dim.categoryId)
          : undefined;

        return {
          ...dim,
          categoryId: dim.categoryId || undefined,
          categoryName: category?.displayName,
          categoryIcon: category?.icon,
          categoryColor: category?.color,
          categorySortOrder: category?.sortOrder || 0,
          // A dimension without a GoIcon percentage has no data.
          percentage: goiconDimensionPcts[dim.id] || 0,
          hasData: dim.id in goiconDimensionPcts
        };
      });
    } else {
      // No wellness data, no GoIcon data, not demo - set all percentages to 0
      enrichedDimensions = state.dimensions.map(dim => {
        const category = dim.categoryId 
          ? state.categories.find(cat => cat.id === dim.categoryId)
          : undefined;
        
        return {
          ...dim,
          categoryId: dim.categoryId || undefined,
          categoryName: category?.displayName,
          categoryIcon: category?.icon,
          categoryColor: category?.color,
          categorySortOrder: category?.sortOrder || 0,
          // Final fallback branch (no CareDaily, no GoIcon, not demo): nothing has data.
          percentage: 0,
          hasData: false
        };
      });
    }
    
    console.log('📊 WellnessPieChart dimensions:', enrichedDimensions);
    console.log('📊 Has wellness data:', hasWellnessData);
    console.log('📊 Using sample data:', usingSampleData);
    
    return (
      <ChunkBoundary onRetry={handlePieChartRetry} rethrowNonChunkErrors>
        <Suspense fallback={<div className="wellness-pie-chart-loading" aria-hidden="true" />}>
          <LazyWellnessPieChart
            dimensions={enrichedDimensions}
            programName={state.programName}
            useCategoryColors={state.programSettings?.useCategoryWheelColors !== false}
          />
        </Suspense>
      </ChunkBoundary>
    );
  };

  const renderActivityLogger = () => {
    if (!state.jwtPayload) return null;

    // For staff users viewing a resident, use the looked-up resident IDs
    // For resident users, use their own JWT IDs
    const residentId = state.currentLocationId || state.jwtPayload.residentId;
    const externalId = state.currentExternalId || state.jwtPayload.externalId;
    
    console.log('🎯 ActivityLogger IDs:', {
      residentId,
      externalId,
      isStaff,
      usingStoredIds: !!state.currentLocationId
    });
    
    return (
      <ActivityLogger
        dimensions={withWheelColors(
          state.dimensions,
          state.categories,
          state.programSettings?.useCategoryWheelColors !== false
        )}
        facilityId={state.jwtPayload.facilityId ? String(state.jwtPayload.facilityId).trim() : ''}
        residentId={residentId}
        externalId={externalId}
        isExpanded={overviewExpandedSection === 'activity'}
        onToggle={(nextExpanded) => setOverviewExpandedSection(nextExpanded ? 'activity' : null)}
        onSubmit={handleActivitySubmit}
      />
    );
  };

  const renderGoalsTracker = () => {
    if (!state.jwtPayload) return null;

    // For staff users viewing a resident, use the looked-up resident location ID
    // For resident users, use their own JWT resident ID
    const residentId = state.currentLocationId || state.jwtPayload.residentId;
    
    // Use the current location ID for CareDaily sync
    const careDailyLocationId = state.currentLocationId || undefined;

    // External ID for goalActivity best-effort logging
    const externalId = state.currentExternalId || state.jwtPayload.externalId || undefined;

    // Wheel-derived nudge: the least-active dimension with real data. Needs at
    // least two dims with data — with fewer there's no meaningful "least".
    // Admin-disableable via "Suggest goals for low wellness areas".
    const nudgeEnabled = state.programSettings?.showBalanceNudge !== false;
    const dimsWithData = nudgeEnabled
      ? (dimensionsWithPercentages as any[]).filter(d => d.hasData)
      : [];
    const lowestDim = dimsWithData.length >= 2
      ? dimsWithData.reduce((a, b) => (b.percentage < a.percentage ? b : a))
      : null;
    // Only nudge when the lowest dimension links to a goal category: without one
    // the suggestion isn't actionable (no goal type maps to it) and adding a
    // goal could never dismiss the tip.
    const balanceHint = lowestDim?.categoryId
      ? { categoryId: lowestDim.categoryId, areaName: lowestDim.categoryName || lowestDim.name }
      : undefined;

    return (
      <GoalsTracker
        residentId={residentId}
        facilityId={state.jwtPayload.facilityId ? String(state.jwtPayload.facilityId).trim() : ''}
        dimensions={state.dimensions}
        careDailyLocationId={careDailyLocationId}
        externalId={externalId}
        isExpanded={overviewExpandedSection === 'goals'}
        onToggle={(nextExpanded) => setOverviewExpandedSection(nextExpanded ? 'goals' : null)}
        balanceHint={balanceHint}
        onGoalsChanged={() => setGoalsVersion(v => v + 1)}
      />
    );
  };

  const buildGoIconDimensionIdToColorMap = (): Record<number, string> => {
    const map: Record<number, string> = {};
    for (const dim of state.dimensions) {
      const id = dim.goiconDimensionId;
      if (!Number.isFinite(id as any)) continue;

      if (dim.categoryId) {
        const cat = state.categories.find(c => c.id === dim.categoryId);
        if (cat?.color) {
          map[Number(id)] = cat.color;
          continue;
        }
      }

      if (dim.color) {
        map[Number(id)] = dim.color;
      }
    }
    return map;
  };

  const buildGoIconDimensionIdToIconMap = (): Record<number, string> => {
    const map: Record<number, string> = {};
    for (const dim of state.dimensions) {
      const id = dim.goiconDimensionId;
      if (!Number.isFinite(id as any)) continue;

      if (dim.categoryId) {
        const cat = state.categories.find(c => c.id === dim.categoryId);
        if (cat?.icon) {
          map[Number(id)] = cat.icon;
          continue;
        }
      }

      if (dim.icon) {
        map[Number(id)] = dim.icon;
      }
    }
    return map;
  };

  const buildCategoryIdToWellnessMap = (): Record<string, RecWellnessDimension> => {
    const allowed: RecWellnessDimension[] = ['mind','body','social','spirit','environment','purpose','financial','other'];
    const map: Record<string, RecWellnessDimension> = {};
    for (const cat of state.categories) {
      if (!cat.id || !cat.categoryKey) continue;
      const key = cat.categoryKey.toLowerCase().trim() as RecWellnessDimension;
      map[cat.id] = allowed.includes(key) ? key : 'other';
    }
    return map;
  };

  // Wellness bucket key ('mind'/'body'/...) -> category display meta, so
  // recommendation badges can be labeled by the GOAL dimension that drove a
  // pick (scorer reasons) rather than only the event's dominant tag.
  const buildWellnessDimensionMeta = (): Record<string, { label: string; color?: string; icon?: string }> => {
    const allowed = ['mind', 'body', 'social', 'spirit', 'environment', 'purpose', 'financial', 'other'];
    const meta: Record<string, { label: string; color?: string; icon?: string }> = {};
    for (const cat of state.categories) {
      const key = (cat.categoryKey || '').toLowerCase().trim();
      if (!allowed.includes(key)) continue;
      meta[key] = { label: cat.displayName || cat.categoryKey, color: cat.color, icon: cat.icon };
    }
    return meta;
  };

  const renderEventRecommendations = () => {
    const residentNeed = buildResidentNeedFromDimensionScores();
    const goiconDimensionIdToWellness = buildGoIconDimensionIdToWellnessMap();
    const goiconDimensionIdToDrivenByLabel = buildGoIconDimensionIdToDrivenByLabelMap();
    const goiconDimensionIdToColor = buildGoIconDimensionIdToColorMap();
    const goiconDimensionIdToIcon = buildGoIconDimensionIdToIconMap();
    const categoryIdToWellness = buildCategoryIdToWellnessMap();
    const facilityId = state.jwtPayload?.facilityId ? String(state.jwtPayload.facilityId).trim() : '';
    const residentId = state.currentLocationId || state.jwtPayload?.residentId || '';
    return (
      <EventRecommendations 
        facilityId={facilityId}
        goiconResidentId={goiconResidentId}
        apiKey={state.jwtPayload?.apiKey}
        isStaff={isStaff}
        isExpanded={overviewExpandedSection === 'recommendations'}
        onToggle={(nextExpanded) => setOverviewExpandedSection(nextExpanded ? 'recommendations' : null)}
        residentNeed={residentNeed}
        recommendationMode={state.programSettings?.recommendationMode ?? 'deficit'}
        residentId={residentId}
        careDailyLocationId={state.currentLocationId || undefined}
        categoryIdToWellness={categoryIdToWellness}
        goiconDimensionIdToWellness={goiconDimensionIdToWellness}
        goiconDimensionIdToDrivenByLabel={goiconDimensionIdToDrivenByLabel}
        goiconDimensionIdToColor={goiconDimensionIdToColor}
        goiconDimensionIdToIcon={goiconDimensionIdToIcon}
        useSmartRecommendations={state.programSettings?.useEventRecommendations === true}
        goalsVersion={goalsVersion}
        wellnessDimensionMeta={buildWellnessDimensionMeta()}
      />
    );
  };

  const renderTabContent = () => {
    console.log(`🎯 Rendering content for tab: ${state.activeTab}`);
    
    // Handle Overview tab specially
    if (state.activeTab === 'overview') {
      return (
        <div className="overview-tab-content">
          {/* Self-assessment notification (only when a resident is in context) */}
          {selfAssessmentConfig?.enabled && selfAssessmentDue && (!isStaff || state.currentExternalId) && (
            <div className="sa-due-notification">
              <div className="sa-due-notification__icon"><Icon name="FaClipboardList" size={18} decorative /></div>
              <div className="sa-due-notification__content">
                <h3>Self-Assessment Due</h3>
                <p>It's time to complete your wellness self-assessment.</p>
              </div>
              <button
                className="sa-due-notification__btn"
                onClick={() => setShowSelfAssessment(true)}
              >
                Start Assessment
              </button>
            </div>
          )}

          {/* Allow-anytime trigger (only when a resident is in context and not already due) */}
          {selfAssessmentConfig?.enabled && selfAssessmentConfig.allowAnytime && !selfAssessmentDue && (!isStaff || state.currentExternalId) && (
            <button
              className="sa-anytime-btn"
              onClick={() => setShowSelfAssessment(true)}
            >
              Take Self-Assessment
            </button>
          )}

          {renderProgressLeader()}
          <div className="wellness-mix-info-wrap">
            {renderWellnessMixChart()}
            <button
              type="button"
              className="help-trigger--wellness-mix info-dot-button"
              aria-label="What do these scores mean?"
              onClick={() => setShowScoreInfo(true)}
            >
              i
            </button>
          </div>
          {renderActivityLogger()}
          {renderGoalsTracker()}
          {renderEventRecommendations()}
        </div>
      );
    }
    
    const metrics = getMetricsForTab(state.activeTab);
    console.log(`📋 Rendering tab "${state.activeTab}" with ${metrics.length} metrics:`, metrics);

    // Render metrics and filter out nulls (metrics with no data)
    const renderedMetrics = metrics.filter(config => config && config.metricKey).map(config => {
          console.log(`🔍 Rendering metric: ${config.metricKey} (${config.displayName}) - visible: ${config.isVisible}`);
          
          // Map config.metricKey to actual data, using per-config sub-key overrides when set
          const metricData = getMetricDataByKey(config.metricKey, config.careDailyValueKey, config.careDailyDisplayKey);
          console.log(`📊 Data for ${config.metricKey}:`, metricData);
          
          // Skip metrics with no data - don't show placeholder cards
          if (!metricData) {
            console.warn(`❌ No data for ${config.metricKey} - skipping (no placeholder)`);
            return null;
          }

          // Threshold value, norms value, status, label gate and basis gate all
          // live in deriveMetricCard so the end-to-end test runs this exact code.
          const { displayValue, colorStatus, customColor, statusLabel, normativeBasis, pointColorStatus } = deriveMetricCard(metricData, config);

          // Filter time-series data to selected date range
          const allTimeSeriesData = state.timeSeriesData?.[config.metricKey] || [];
          // Data points are UTC midnight, so anchor the cutoff to a UTC day
          // boundary. Subtracting raw milliseconds from now() lands mid-day and
          // excludes the oldest day of the window.
          const today = new Date();
          const cutoffDate = new Date(Date.UTC(
            today.getUTCFullYear(),
            today.getUTCMonth(),
            today.getUTCDate() - dateRange.days
          ));
          const metricTimeSeriesData = allTimeSeriesData.filter(dataPoint =>
            new Date(dataPoint.date) >= cutoffDate
          );

          console.log(`📅 Filtering ${config.metricKey}:`);
          console.log(`  - All data: ${allTimeSeriesData.length} points`);
          console.log(`  - Cutoff date: ${cutoffDate.toISOString().slice(0, 10)}`);
          console.log(`  - Filtered data: ${metricTimeSeriesData.length} points`);
          console.log(`  - Date range: ${dateRange.label}`);
          
          // Fallback: if no data in selected range, show all available data
          let finalTimeSeriesData = metricTimeSeriesData;
          let effectiveDateRangeLabel = dateRange.label;
          let fallbackNotice: string | undefined;

          if (metricTimeSeriesData.length === 0 && allTimeSeriesData.length > 0) {
            finalTimeSeriesData = allTimeSeriesData.slice(-1);
            const lastDate = new Date(allTimeSeriesData[allTimeSeriesData.length - 1].date);
            const lastDateStr = lastDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
            effectiveDateRangeLabel = `Most Recent Data`;
            fallbackNotice = `No data in ${dateRange.label.toLowerCase()}. Showing most recent value as of ${lastDateStr}`;
          } else if (metricTimeSeriesData.length === 1) {
            const lastDate = new Date(metricTimeSeriesData[0].date);
            const lastDateStr = lastDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' });
            effectiveDateRangeLabel = `Latest Value`;
            fallbackNotice = `Only data point in ${dateRange.label.toLowerCase()}. Value as of ${lastDateStr}`;
          }

          const scaledTimeSeriesData = config.displayAs0to100
            ? finalTimeSeriesData.map(pt => ({ ...pt, value: pt.value * 100 }))
            : finalTimeSeriesData;

          // Compute highest configured threshold upper bound to cap chart axes.
          // Only include values that are actually set (not undefined/null).
          const thresholdUppers = [config.greenMax, config.yellowMax, config.redMax]
            .filter((v): v is number => typeof v === 'number' && isFinite(v));
          const configRangeMax = thresholdUppers.length > 0
            ? Math.max(...thresholdUppers)
            : undefined; // undefined = unbounded, charts use their own dynamic scaling

          return (
            <MetricCard
              key={config.id}
              title={config.displayName}
              value={displayValue}
              targetValue={config.targetValue}
              targetMin={config.targetMin}
              targetMax={config.targetMax}
              colorStatus={colorStatus}
              units={config.units ?? undefined}
              visualizationType={config.visualizationType}
              zscore={metricData.zscore}
              icon={metricData.metadata?.icon}
              metadata={metricData.metadata}
              statusLabel={statusLabel}
              normativeBasis={normativeBasis}
              pointColorStatus={pointColorStatus}
              tooltipText={config.helpText ?? undefined}
              showInlineNote={config.showInlineNote === true}
              timeSeriesData={scaledTimeSeriesData}
              dateRangeLabel={effectiveDateRangeLabel}
              timeSeriesFallbackNotice={fallbackNotice}
              configRangeMax={configRangeMax}
              customColor={customColor}
              displayAs0to100={config.displayAs0to100 === true}
              thresholds={{
                greenMin: config.greenMin,
                greenMax: config.greenMax,
                yellowMin: config.yellowMin,
                yellowMax: config.yellowMax,
                redMin: config.redMin,
                redMax: config.redMax,
              }}
            />
          );
        }).filter(metric => metric !== null); // Remove null entries (metrics with no data)

    const scoreInfoTriggerRow = (
      <div className="metrics-tab-header">
        <button
          type="button"
          className="help-trigger help-trigger--labeled"
          aria-label="What do these scores mean?"
          onClick={() => setShowScoreInfo(true)}
        >
          <Icon name="FaInfoCircle" size={16} />
          <span>What do these scores mean?</span>
        </button>
      </div>
    );

    // If all metrics were filtered out (no data available), show empty state
    if (renderedMetrics.length === 0) {
      return (
        <>
          {scoreInfoTriggerRow}
          <div className="empty-tab">
            <div className="empty-icon"><Icon name="FaChartLine" size={32} /></div>
            <p>No wellness data available for this category.</p>
          </div>
        </>
      );
    }

    return (
      <>
        {scoreInfoTriggerRow}
        <div className="metrics-grid">
          {renderedMetrics}
        </div>
      </>
    );
  };

  const getMetricDataByKey = (metricKey: string, valueKey?: string | null, displayKey?: string | null) =>
    lookupMetricData(state.wellnessData, metricKey, valueKey, displayKey);

  // Loading state
  if (state.loading) {
    return (
      <div className="dashboard-container">
        <LoadingSpinner message="Loading wellness data..." />
      </div>
    );
  }

  // Error state
  if (state.error) {
    return (
      <div className="dashboard-container">
        <ErrorMessage 
          message={state.error}
          onRetry={initializeDashboard}
        />
      </div>
    );
  }

  // Removed renderOtherMetricsContent - all categories are now treated equally

  // Create tabs array with Overview as first tab
  const createTabsArray = () => {
    const overviewTab = {
      id: 'overview',
      label: (
        <>
          <Icon name="FaChartLine" size={16} />
          <span>Overview</span>
        </>
      ),
      metrics: []
    };
    
    const categoryTabs = state.categories.map(cat => ({
      id: cat.id, // Use UUID instead of categoryKey
      label: (
        <>
          <Icon name={cat.icon || 'FaChartLine'} size={16} />
          <span>{cat.displayName}</span>
        </>
      ),
      metrics: []
    }));
    
    return [overviewTab, ...categoryTabs];
  };

  return (
    <div className="dashboard-container">
      <h1 className="sr-only">{state.programSettings?.programName || 'Wellness'} Dashboard</h1>
      {isDemoMode() && (
        <div className="demo-mode-banner">
          <span className="demo-mode-icon"><Icon name="FaTheaterMasks" size={24} decorative /></span>
          <span className="demo-mode-text">{getDemoModeMessage()}</span>
        </div>
      )}
      
      {/* Staff debug modal */}
      {isStaff && showDebugPanel && (
        <div className="debug-overlay" onClick={() => setShowDebugPanel(false)}>
          <div className="debug-modal" onClick={(e) => e.stopPropagation()}>
            <div className="debug-modal-header">
              <h2>CareDaily API Debug <span style={{ fontSize: '0.75rem', fontWeight: 400, opacity: 0.6 }}>Staff Only</span></h2>
              <button className="debug-modal-close" onClick={() => setShowDebugPanel(false)} aria-label="Close debug panel"><Icon name="FaTimes" size={18} /></button>
            </div>
            <div className="debug-modal-body">
              <div className="debug-location-override">
                <label className="debug-override-label">
                  Location ID Override
                  <span className="debug-override-hint">Bypasses external ID lookup — useful for sandbox testing</span>
                </label>
                <div className="debug-override-row">
                  <input
                    className="debug-override-input"
                    type="text"
                    placeholder="e.g. 289092"
                    value={debugLocationInput}
                    onChange={e => setDebugLocationInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') setDebugLocationOverride(debugLocationInput.trim());
                    }}
                  />
                  <button
                    className="debug-override-apply"
                    onClick={() => setDebugLocationOverride(debugLocationInput.trim())}
                  >
                    Apply
                  </button>
                  {debugLocationOverride && (
                    <button
                      className="debug-override-clear"
                      onClick={() => { setDebugLocationOverride(''); setDebugLocationInput(''); }}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {debugLocationOverride && (
                  <p className="debug-override-active">
                    <Icon name="FaCheck" size={12} /> Active override: <code>{debugLocationOverride}</code>
                  </p>
                )}
              </div>
              <div className="debug-location-override">
                <label className="debug-override-label">
                  External ID Override
                  <span className="debug-override-hint">Used for goalActivity and other external-user calls when you are testing with a manual location ID</span>
                </label>
                <div className="debug-override-row">
                  <input
                    className="debug-override-input"
                    type="text"
                    placeholder="e.g. 123456"
                    value={debugExternalIdInput}
                    onChange={e => setDebugExternalIdInput(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') setDebugExternalIdOverride(debugExternalIdInput.trim());
                    }}
                  />
                  <button
                    className="debug-override-apply"
                    onClick={() => setDebugExternalIdOverride(debugExternalIdInput.trim())}
                  >
                    Apply
                  </button>
                  {debugExternalIdOverride && (
                    <button
                      className="debug-override-clear"
                      onClick={() => { setDebugExternalIdOverride(''); setDebugExternalIdInput(''); }}
                    >
                      Clear
                    </button>
                  )}
                </div>
                {debugExternalIdOverride && (
                  <p className="debug-override-active">
                    <Icon name="FaCheck" size={12} /> Active external ID: <code>{debugExternalIdOverride}</code>
                  </p>
                )}
              </div>
              <hr className="debug-divider" />
              {state.currentLocationId && (
                <div className="debug-fetch-actions">
                  <button
                    className="debug-override-apply"
                    onClick={async () => {
                      try {
                        const facilityId = state.jwtPayload?.facilityId ? String(state.jwtPayload.facilityId).trim() : '';
                        const svc = await createCareDailyService(facilityId);
                        const goalsRaw = await svc.makeRequest<any>(
                          `/locations/${state.currentLocationId}/state`,
                          'GET',
                          { name: 'goals' }
                        );
                        setDebugRawResponses(prev => ({ ...prev, goalsState: goalsRaw }));
                      } catch (e) {
                        setDebugRawResponses(prev => ({ ...prev, goalsState: { error: e instanceof Error ? e.message : String(e) } }));
                      }
                    }}
                  >
                    Refresh Goals
                  </button>
                </div>
              )}
              {Object.keys(debugRawResponses).length === 0 ? (
                <p className="debug-empty">No API responses captured yet. Select a resident to load data.</p>
              ) : (
                Object.entries(debugRawResponses).map(([key, value]) => (
                  <details key={key} className="debug-section" open={key === 'locationId'}>
                    <summary className="debug-section-title">
                      <span>{key}</span>
                      <button
                        className="debug-copy-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          navigator.clipboard.writeText(JSON.stringify(value, null, 2));
                        }}
                      >
                        Copy
                      </button>
                    </summary>
                    <pre className="debug-json">{JSON.stringify(value, null, 2)}</pre>
                  </details>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-content">
        {/* Resident selector for STAFF users */}
        {isStaff && state.jwtPayload && (
          <ResidentSelector
            currentResidentId={selectedResidentId}
            facilityId={state.jwtPayload.facilityId ? String(state.jwtPayload.facilityId).trim() : ''}
            apiKey={state.jwtPayload.apiKey}
            onResidentChange={setSelectedResidentId}
            onGoiconResidentIdChange={setGoiconResidentId}
          />
        )}
        
        
        {/* CareDaily API connection error banner */}
        {(() => {
          const err = state.careDailyConnectionError;
          if (!err || err === 'NO_API_KEY_STAFF' || err === 'NO_RESIDENT_SELECTED') return null;

          let title = 'Wellness data not yet available';
          let body = 'Something went wrong while loading wellness data. If this keeps happening, please contact support.';
          // Retry only helps for technical/transient failures. Enrollment and
          // permission problems won't resolve by reloading, so no button there.
          let retryable = true;

          if (err === 'RESIDENT_NOT_IN_CAREDAILY') {
            title = 'No wellness profile found';
            body = 'This resident hasn\'t been enrolled in CareDaily yet. Contact your administrator to get their wellness profile set up.';
            retryable = false;
          } else if (err === 'CAREDAILY_TRANSIENT') {
            title = 'Wellness data didn\'t load';
            body = 'There was a temporary problem loading this resident\'s wellness data.';
          } else if (err === 'ACCESS_DENIED') {
            title = 'Access not permitted';
            body = 'Your account doesn\'t have permission to view this resident\'s wellness data. Contact your administrator if you believe this is a mistake.';
            retryable = false;
          }

          return (
            <div className="caredaily-connection-error">
              <div className="error-icon" aria-hidden="true">ℹ️</div>
              <div className="error-content">
                <h3>{title}</h3>
                <p>{body}</p>
                {retryable && (
                  <button
                    type="button"
                    className="caredaily-connection-error__retry"
                    onClick={() => initializeDashboard()}
                    disabled={state.loading}
                  >
                    {state.loading ? 'Retrying…' : 'Try again'}
                  </button>
                )}
              </div>
            </div>
          );
        })()}
        
        <TabNavigation
          tabs={createTabsArray()}
          activeTab={state.activeTab}
          onTabChange={handleTabChange}
          trailingContent={
            <button
              type="button"
              className="help-trigger help-trigger--labeled tab-trailing-trigger"
              aria-label="Privacy and your information"
              onClick={() => setShowPrivacyFaq(true)}
            >
              <Icon name="FaShieldAlt" size={16} />
              <span>Privacy</span>
            </button>
          }
          rightAction={isStaff && onNavigateToAdmin ? {
            label: 'Admin',
            onClick: onNavigateToAdmin,
            icon: <Icon name="FaCog" size={16} />
          } : undefined}
        />

        {isStaff && (
          <div className="debug-bar">
            <button
              className="debug-toggle-btn"
              onClick={() => setShowDebugPanel(true)}
              title="Show raw CareDaily API responses"
            >
              <Icon name="FaWrench" size={12} /> API Debug <span style={{ fontSize: '0.7rem', opacity: 0.7, marginLeft: '0.25rem' }}>(Staff Only)</span>
            </button>
          </div>
        )}
        
        {/* Category description - only show for category tabs (not overview) */}
        {state.activeTab !== 'overview' && (() => {
          const currentCategory = state.categories.find(cat => cat.id === state.activeTab || cat.categoryKey === state.activeTab);
          const description = currentCategory?.description?.trim();
          return description ? (
            <div className="category-description">
              <p>{description}</p>
            </div>
          ) : null;
        })()}
        
        {/* Date range selector - only show when not on overview tab */}
        {state.activeTab !== 'overview' && renderDateRangeSelector()}
        
        <div className="tab-content">
          {renderTabContent()}
        </div>
      </div>

      {/* Self-Assessment Modal */}
      {showSelfAssessment && state.jwtPayload && (
        <SelfAssessmentModal
          isOpen={showSelfAssessment}
          metrics={state.metricConfigs.filter(m => m.isVisible && m.selfAssessmentPrompt)}
          facilityId={state.jwtPayload.facilityId ? String(state.jwtPayload.facilityId).trim() : ''}
          residentId={state.currentExternalId || state.jwtPayload.residentId}
          respondedBy={state.jwtPayload.residentId}
          respondedByRole={isStaff ? 'STAFF' : 'resident'}
          primaryColor={state.primaryColor}
          secondaryColor={state.secondaryColor}
          onClose={() => setShowSelfAssessment(false)}
          onComplete={() => {
            setShowSelfAssessment(false);
            setSelfAssessmentDue(false);
            initializeDashboard();
          }}
        />
      )}

      <ScoreInfoDialog
        isOpen={showScoreInfo}
        onClose={() => setShowScoreInfo(false)}
        sections={getScoreInfoSections()}
      />

      <PrivacyFaq
        isOpen={showPrivacyFaq}
        onClose={() => setShowPrivacyFaq(false)}
        sections={helpContent?.privacyFaq}
      />
    </div>
  );
};
